import React from 'react';
import { 
  Typography, 
  Box,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import PageTemplate from '../components/PageTemplate';

const TermsPage: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const lastUpdated = "March 15, 2024";
  
  return (
    <PageTemplate 
      title="Terms of Service" 
      description={`Last updated: ${lastUpdated}`}
      breadcrumbs={[{ label: 'Terms of Service', path: '/terms' }]}
    >
      <Box sx={{ '& > h6': { mt: 4, mb: 2 } }}>
        <Typography variant="body1" paragraph>
          Welcome to 1TimeMail. These Terms of Service ("Terms") govern your access to and use of the 1TimeMail website and services (the "Service"). Please read these Terms carefully. By accessing or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Service.
        </Typography>

        <Typography variant="h6" fontWeight={600}>
          1. ACCEPTANCE OF TERMS
        </Typography>
        
        <Typography variant="body1" paragraph>
          By accessing or using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms. We reserve the right to change these Terms at any time. We will provide notice of any changes by updating the "Last Updated" date at the top of these Terms. Your continued use of the Service following the posting of revised Terms means that you accept and agree to the changes.
        </Typography>
        
        <Typography variant="h6" fontWeight={600}>
          2. DESCRIPTION OF SERVICE
        </Typography>
        
        <Typography variant="body1" paragraph>
          1TimeMail provides temporary email services that allow users to create disposable email addresses for receiving emails. The Service is provided "as is" and "as available" without warranties of any kind, either express or implied.
        </Typography>
        
        <Typography variant="body1" paragraph>
          Our free service includes access to basic email features with a 24-hour retention period.
        </Typography>
        
        <Typography variant="body1" paragraph>
          Free accounts have access to basic features with certain limitations, while Premium and Business accounts have access to additional features as described on our pricing page.
        </Typography>
        
        <Typography variant="h6" fontWeight={600}>
          3. ACCOUNT TERMS
        </Typography>
        
        <List>
          <ListItem>
            <ListItemText
              primary="3.1 Free Accounts"
              secondary="Free accounts do not require registration or personal information. Temporary email addresses created under free accounts expire after 24 hours."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="3.2 Premium and Business Accounts"
              secondary="If you sign up for Premium or Business accounts, you must provide accurate and complete information. You are responsible for maintaining the security of your account credentials and for all activities that occur under your account."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="3.3 Age Requirement"
              secondary="You must be at least 13 years old to use the Service. If you are under 18, you must have permission from a parent or guardian to use the Service."
            />
          </ListItem>
        </List>
        
        <Typography variant="h6" fontWeight={600}>
          4. ACCEPTABLE USE
        </Typography>
        
        <Typography variant="body1" paragraph>
          You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to use the Service:
        </Typography>
        
        <List>
          <ListItem>
            <ListItemText primary="In any way that violates any applicable federal, state, local, or international law or regulation." />
          </ListItem>
          <ListItem>
            <ListItemText primary="To send, knowingly receive, upload, download, use, or re-use any material that is illegal, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable." />
          </ListItem>
          <ListItem>
            <ListItemText primary="To impersonate or attempt to impersonate 1TimeMail, a 1TimeMail employee, another user, or any other person or entity." />
          </ListItem>
          <ListItem>
            <ListItemText primary="To engage in any activity that interferes with or disrupts the Service (or the servers and networks connected to the Service)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="To attempt to circumvent any security measures implemented by 1TimeMail." />
          </ListItem>
          <ListItem>
            <ListItemText primary="For spamming, phishing, or any other unauthorized mass communication." />
          </ListItem>
          <ListItem>
            <ListItemText primary="For illegal activities, including but not limited to fraud, scams, or distribution of malware." />
          </ListItem>
        </List>
        
        <Typography variant="body1" paragraph>
          We reserve the right to terminate your access to the Service immediately, without prior notice, if in our sole discretion you violate any of the restrictions above.
        </Typography>
        
        <Typography variant="h6" fontWeight={600}>
          5. INTELLECTUAL PROPERTY
        </Typography>
        
        <Typography variant="body1" paragraph>
          The Service and its original content, features, and functionality are and will remain the exclusive property of 1TimeMail and its licensors. The Service is protected by copyright, trademark, and other laws of both the United States and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of 1TimeMail.
        </Typography>
        
        <Typography variant="h6" fontWeight={600}>
          6. USER CONTENT
        </Typography>
        
        <Typography variant="body1" paragraph>
          You understand that all information, data, text, or other materials that you receive, send, or store through the Service ("User Content") are your sole responsibility. You, and not 1TimeMail, are entirely responsible for all User Content that you upload, post, email, transmit, or otherwise make available via the Service.
        </Typography>
        
        <Typography variant="body1" paragraph>
          By using the Service, you grant 1TimeMail a limited license to store and process your User Content solely for the purpose of providing the Service to you. We do not claim ownership of your User Content.
        </Typography>
        
        <Typography variant="h6" fontWeight={600}>
          7. PRIVACY
        </Typography>
        
        <Typography variant="body1" paragraph>
          Your use of the Service is also governed by our Privacy Policy, which can be found at <a href="/privacy" style={{ color: 'inherit', fontWeight: 600 }}>1timemail.org/privacy</a>. Please review our Privacy Policy to understand our practices regarding your personal information.
        </Typography>
        
        <Typography variant="h6" fontWeight={600}>
          8. SUBSCRIPTION AND BILLING
        </Typography>
        
        <Typography variant="body1" paragraph>
          Some aspects of the Service may be provided for a fee. If you elect to use paid aspects of the Service, you agree to pay all applicable fees as described on our pricing page.
        </Typography>
        
        <List>
          <ListItem>
            <ListItemText
              primary="8.1 Billing"
              secondary="For Premium and Business subscriptions, payment will be charged to your chosen payment method at confirmation of purchase and at the beginning of each renewal period."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="8.2 Automatic Renewal"
              secondary="Subscriptions automatically renew unless auto-renew is turned off at least 24 hours before the end of the current period."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="8.3 Cancellation"
              secondary="You can cancel your subscription at any time by accessing your account settings. No refunds will be issued for partial subscription periods."
            />
          </ListItem>
        </List>
        
        <Typography variant="h6" fontWeight={600}>
          9. LIMITATION OF LIABILITY
        </Typography>
        
        <Typography variant="body1" paragraph>
          In no event shall 1TimeMail, its officers, directors, employees, or agents, be liable to you for any direct, indirect, incidental, special, punitive, or consequential damages whatsoever resulting from:
        </Typography>
        
        <List>
          <ListItem>
            <ListItemText primary="Your use of or inability to use the Service." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Any unauthorized access to or use of our servers and/or any information stored therein." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Any interruption or cessation of transmission to or from the Service." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Any bugs, viruses, or the like, which may be transmitted to or through the Service by any third party." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Any loss of data, including emails and attachments." />
          </ListItem>
        </List>
        
        <Typography variant="body1" paragraph>
          This limitation of liability shall apply to the fullest extent permitted by law in the applicable jurisdiction.
        </Typography>
        
        <Typography variant="h6" fontWeight={600}>
          10. DISCLAIMER OF WARRANTIES
        </Typography>
        
        <Typography variant="body1" paragraph>
          The Service is provided on an "AS IS" and "AS AVAILABLE" basis. 1TimeMail expressly disclaims all warranties of any kind, whether express or implied, including, but not limited to, the implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
        </Typography>
        
        <Typography variant="body1" paragraph>
          1TimeMail makes no warranty that (i) the Service will meet your requirements, (ii) the Service will be uninterrupted, timely, secure, or error-free, (iii) the results that may be obtained from the use of the Service will be accurate or reliable, or (iv) any errors in the Service will be corrected.
        </Typography>
        
        <Typography variant="h6" fontWeight={600}>
          11. TERMINATION
        </Typography>
        
        <Typography variant="body1" paragraph>
          We may terminate or suspend your access to the Service immediately, without prior notice or liability, for any reason whatsoever, including, without limitation, if you breach these Terms.
        </Typography>
        
        <Typography variant="body1" paragraph>
          Free email addresses automatically expire after 24 hours. Premium and Business email addresses expire according to the subscription plan selected.
        </Typography>
        
        <Typography variant="h6" fontWeight={600}>
          12. GOVERNING LAW
        </Typography>
        
        <Typography variant="body1" paragraph>
          These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions. Any dispute arising from or relating to these Terms or your use of the Service shall be resolved exclusively in the state or federal courts located in San Francisco County, California.
        </Typography>
        
        <Typography variant="h6" fontWeight={600}>
          13. CHANGES TO TERMS
        </Typography>
        
        <Typography variant="body1" paragraph>
          We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
        </Typography>
        
        <Typography variant="h6" fontWeight={600}>
          14. CONTACT INFORMATION
        </Typography>
        
        <Typography variant="body1" paragraph>
          If you have any questions about these Terms, please contact us at:
        </Typography>
        
        <Typography variant="body1" paragraph>
          Email: terms@1timemail.org<br />
          Address: 1TimeMail Legal Department, 123 Privacy Ave, Suite 456, San Francisco, CA 94105
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          © {currentYear} 1TimeMail. All rights reserved.
        </Typography>
      </Box>
    </PageTemplate>
  );
};

export default TermsPage; 